'use client';
import { useState, useRef, useEffect } from "react";
import ChatInterface from "./chat-interface";
import ChatResponses from "./chat-responses";
import { Message } from "@/schemas/message-schema";
import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useChatSession } from "@/hooks/use-chat-session";
import QuickPrompts from "./quick-prompts";
import Menu from "./menu";
import AboutPage from "./about";
import { AnimatePresence } from "framer-motion";

export default function ChatWindow() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [isFrontpage, setIsFrontpage] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const chatInterfaceContainerRef = useRef<HTMLDivElement>(null);
    const { contextSafe } = useGSAP({ scope: scrollContainerRef });

    useGSAP(() => {
        if (scrollContainerRef.current) {
            const tl = gsap.timeline();
            const anim1 = gsap.fromTo('#greeting',
                { opacity: "0", y: 150 },
                {
                    opacity: "1",
                    y: 0,
                    duration: .5,
                    ease: "power3.out",
                }
            );
            const anim2 = gsap.fromTo('#title',
                { opacity: "0", y: 150 },
                {
                    opacity: "1",
                    y: 0,
                    duration: .5,
                    ease: "power3.out",
                }
            );
            const anim3 = gsap.fromTo('#intro',
                { opacity: "0", y: 150 },
                {
                    opacity: "1",
                    y: 0,
                    duration: .5,
                    ease: "power3.out",
                }
            );

            tl.add(anim1, 0.5);
            tl.add(anim2, 0.6);
            tl.add(anim3, 0.7);
            tl.add(gsap.fromTo("#quick-prompts",
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: .5,
                    ease: "power3.out",
                }
            ), 0.9);
            tl.add(gsap.to("#more-prompts",
                {
                    opacity: 1,
                    duration: .5,
                    ease: "power3.out",
                }
            ), 0.3);
        }
    }, { scope: scrollContainerRef });


    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            // Show if we are more than 100px away from the bottom
            const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 100;
            setShowScrollButton(isNotAtBottom);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToBottom = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight - 400,
                behavior: 'smooth'
            });
        }
    };

    const animateOut = contextSafe(() => {
        return async () => {
            const tl = gsap.timeline();
            const anim1 = gsap.to('#title',
                {
                    opacity: "0",
                    y: -150,
                    duration: .1,
                    ease: "sine.out",
                }
            );
            const anim2 = gsap.to('#intro',
                {
                    opacity: "0",
                    y: -150,
                    duration: .1,
                    ease: "sine.out",
                }
            );
            const anim3 = gsap.to('#greeting',
                {
                    opacity: "0",
                    y: -150,
                    duration: .1,
                    ease: "sine.out",
                }
            );
            tl.add(anim3, 0.0);
            tl.add(anim1, 0.1);
            tl.add(anim2, 0.2);
            tl.add(
                gsap.to('#quick-prompts',
                    {
                        height: '0',
                        duration: .2,
                        ease: "sine.out",
                    }
                ), 0.2);
            tl.addPause(0.4);
            await tl;
        };
    });


    async function updateMessages(newMessage: Message) {
        if (chatInterfaceContainerRef.current && isFrontpage) {
            await animateOut()();
            gsap.set('#welcome', { display: 'none' });
            gsap.set('#chat-response', { display: 'flex' });
            setIsFrontpage(false);
        }

        setMessages((prev) => {
            // If the message has an ID, check if it exists
            if (newMessage.id) {
                const existingIndex = prev.findIndex(m => m.id === newMessage.id);
                if (existingIndex !== -1) {
                    // Update existing message
                    const newMessages = [...prev];
                    // Only update content to avoid unnecessary re-renders if other props didn't change
                    if (newMessages[existingIndex].content !== newMessage.content ||
                        newMessages[existingIndex].isStreaming !== newMessage.isStreaming ||
                        JSON.stringify(newMessages[existingIndex].nextQuestions) !== JSON.stringify(newMessage.nextQuestions)
                    ) {
                        newMessages[existingIndex] = {
                            ...newMessages[existingIndex],
                            ...newMessage
                        };
                        return newMessages;
                    }
                    return prev;
                }
            }

            // If no ID or not found, append
            // Ensure we don't duplicate if we somehow get the same ID but findIndex failed (unlikely)
            return [...prev, newMessage];
        });
    }

    async function onHomeClick() {
        window.location.reload();
    }

    const { sendMessage, isLoading } = useChatSession({ onMessagesChange: updateMessages });

    return (
        <div className="w-screen h-dvh relative">
            <Menu onHomeClick={onHomeClick} sendMessage={sendMessage} onAboutClick={() => setShowAbout(!showAbout)} isOpen={showAbout} />
            <AnimatePresence>
                {showAbout && <AboutPage onClose={() => setShowAbout(false)} />}
            </AnimatePresence>
            <div
                ref={scrollContainerRef}
                className="w-screen h-dvh grid justify-center overflow-hidden overscroll-none  grid-rows-[auto_min-content_auto] md:grid-rows-[1fr_min-content_auto]"
            >

                {/* Row 1 */}
                <div className="w-screen h-full overflow-hidden min-h-0">

                    <div id="welcome" className="flex items-end-safe justify-center w-full h-full overflow-hidden">
                        <div id="welcome-top" className="w-full max-w-4xl min-w-[320px]">
                            <div id="greeting" className="opacity-0 pb-2 text-muted-foreground px-10 md:px-22 text-center md:text-left">
                                Hello there, I am David.
                            </div>
                            <div id="title" className="font-heading opacity-0 text-[12vw] leading-[11vw] font-semibold mb-2 md:leading-20 md:text-right md:w-fit md:text-8xl px-0 md:px-10 text-center">
                                I think <span className="text-[#FFC700]">product</span>.<br />I speak <span className="text-[#1581BF]">tech</span>.
                            </div>
                            <div id="intro" className="opacity-0 md:text-xl mt-6 mb-6 md:mb-4 w-full px-8 md:px-16 md:text-left text-center">
                                With product polish & technical fluency I shape ideas into systems, lead engineering teams through complexity, and align product vision with technical reality.
                            </div>
                        </div>
                    </div>


                    <div id="chat-response" className="hidden w-full h-full relative z-0 overscroll-none">
                        <div className="w-full h-full overscroll-none"><ChatResponses messages={messages} sendMessage={sendMessage} /></div>
                    </div>
                </div>


                {/* Row 2 */}
                <div className="w-full flex justify-center">
                    <div id="chat-interface-container" ref={chatInterfaceContainerRef} className="z-100 bottom-[calc(60%-120px)] md:bottom-[calc(50%-120px)] w-full max-w-4xl min-w-[320px] pointer-events-auto">
                        {showScrollButton && (
                            <Button
                                variant="outline"
                                size="icon"
                                className="absolute -top-12 left-1/2 -translate-x-1/2 rounded-full shadow-md bg-background hover:bg-muted"
                            // onClick={scrollToBottom}
                            >
                                <ArrowDown className="h-4 w-4" />
                            </Button>
                        )}
                        <ChatInterface sendMessage={sendMessage} isLoading={isLoading} isFrontpage={isFrontpage} />
                    </div>
                </div>

                {/* Row 3 */}
                <div id="quick-prompts" className="opacity-0 px-10 w-screen h-[10dvh] md:h-[30dvh] xl:h-[30dvh] 2xl:h-[40dvh] overflow-hidden flex justify-center">
                    <QuickPrompts sendMessage={sendMessage} />
                </div>

            </div>
        </div>
    );
}
